'use client';

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  UserCheck, 
  Zap, 
  Activity, 
  Shirt, 
  Sparkles, 
  CalendarClock, 
  AlertCircle 
} from 'lucide-react';

const inclusions = [
  {
    title: 'Certified Personal Training',
    icon: UserCheck,
    description: 'Every 20-minute session is one-on-one with a trained EMS specialist. No group classes. No unsupervised workouts.',
  },
  {
    title: 'Miha Bodytec Technology',
    icon: Zap,
    description: 'German-engineered, medical-grade equipment used by Olympic athletes. Not consumer-grade gadgets.',
  },
  {
    title: 'Body Composition Analysis',
    icon: Activity,
    description: 'Regular diagnostic assessments tracking muscle mass, fat percentage, and metabolic rate. Real data.',
  },
  {
    title: 'Complete Equipment Provision',
    icon: Shirt,
    description: 'Training suit, towels, water, and post-workout protein. You arrive with nothing but yourself.',
  },
  {
    title: 'Hospital-Grade Hygiene',
    icon: Sparkles,
    description: 'Every suit is sanitized after every session. We maintain clinical cleanliness throughout the studio.',
  },
  {
    title: 'Flexible Scheduling',
    icon: CalendarClock,
    description: 'Book sessions at times that work for your calendar, including early mornings and evenings.',
  },
];

export default function MembershipValue() {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="bg-black text-white py-16 md:py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      
      {/* Background Ambience */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#faaf40] opacity-[0.05] blur-[150px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500 opacity-[0.02] blur-[150px] rounded-full pointer-events-none" />

      {/* Main Container */}
      <div className="max-w-7xl mx-auto relative z-10">

        {/* --- PART 1: THE PHILOSOPHY --- */}
        <div className="mb-20">
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-16"
            >
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
                    Why We Don't Charge <br className="hidden sm:block" />
                    <span className="text-[#faaf40]">Gym Fees</span>
                </h2>
                <div className="h-1.5 w-24 bg-[#faaf40] mx-auto rounded-full shadow-[0_0_15px_#faaf40]" />
            </motion.div>

            {/* Split Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">
                
                {/* Left: The Industry Problem */}
                <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 }}
                    className="bg-neutral-900 p-6 rounded-2xl border border-red-900/50 shadow-[0_0_30px_rgba(220,38,38,0.05)] relative overflow-hidden"
                >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-red-600/10 blur-[50px] rounded-full pointer-events-none" />
                    
                    <div className="flex items-center gap-4 mb-4 relative z-10">
                        <div className="bg-red-900/20 p-2 rounded-lg border border-red-500/20">
                            <AlertCircle className="text-red-500 w-5 h-5" />
                        </div>
                        <h3 className="text-lg font-bold text-white">The Traditional Model</h3>
                    </div>
                    <p className="text-neutral-300 text-sm leading-relaxed mb-4 relative z-10">
                        Traditional gyms profit from members who <span className="text-red-400 font-semibold">don't show up</span>. They oversell capacity, knowing most people will abandon their resolutions.
                    </p>
                    <p className="text-neutral-300 text-sm leading-relaxed relative z-10">
                        Their business model depends on your failure. If everyone who paid showed up, the gym would collapse.
                    </p>
                </motion.div>

                {/* Right: The Aura Way */}
                <motion.div 
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 }}
                    className="p-2"
                >
                     <h3 className="text-3xl font-bold text-white mb-4">
                        The <span className="text-[#faaf40]">Aura</span> Principle
                    </h3>
                    <p className="text-neutral-200 text-base leading-relaxed mb-4">
                        We accept only the number of members we can genuinely serve well. Every session requires our full attention—<span className="text-white font-semibold border-b-2 border-[#faaf40]">one trainer, one client, one focused workout.</span>
                    </p>
                    <p className="text-neutral-200 text-base leading-relaxed">
                        Comparing Aura to a gym membership misses the point. The honest comparison is <span className="text-white font-bold">Personal Training</span>.
                    </p>
                </motion.div>
            </div>
        </div>

        {/* --- PART 2: THE INCLUSIONS GRID (COMPACT WIDTH & SIZE) --- */}
        {/* Added max-w-5xl mx-auto here to constrain the width of the grid */}
        <div className="max-w-5xl mx-auto">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} 
                className="text-center mb-10"
            >
                <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                    What Every Membership Includes
                </h3>
                <p className="text-neutral-400 text-base">Standard with every tier. No hidden fees.</p>
            </motion.div>

            {/* Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {inclusions.map((item, idx) => {
                    const isActive = activeIndex === idx;

                    return (
                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: idx * 0.1 }}
                            onMouseEnter={() => setActiveIndex(idx)}
                            onClick={() => setActiveIndex(idx)}
                            className={`
                                cursor-pointer relative p-5 rounded-xl transition-all duration-300 flex flex-col h-full group
                                ${isActive 
                                    ? 'bg-neutral-900 border-2 border-[#faaf40] shadow-[0_0_25px_rgba(250,175,64,0.15)] scale-[1.02] z-10' 
                                    : 'bg-neutral-900/60 border border-neutral-700 hover:border-neutral-500 hover:bg-neutral-800'
                                }
                            `}
                        >
                            {/* Active Gradient Background Overlay */}
                            {isActive && (
                                <div className="absolute inset-0 bg-gradient-to-br from-[#faaf40]/10 to-transparent rounded-xl pointer-events-none" />
                            )}

                            {/* Icon Box */}
                            <div className={`
                                w-10 h-10 rounded-lg flex items-center justify-center mb-4 transition-all duration-300 relative z-10
                                ${isActive 
                                    ? 'bg-[#faaf40] text-black shadow-[0_0_15px_rgba(250,175,64,0.4)]' 
                                    : 'bg-neutral-950 border border-neutral-700 text-neutral-400 group-hover:border-neutral-500 group-hover:text-white'
                                }
                            `}>
                                <item.icon className="w-5 h-5" />
                            </div>

                            {/* Text Content */}
                            <h4 className={`text-lg font-bold mb-2 relative z-10 ${isActive ? 'text-white' : 'text-neutral-100'}`}>
                                {item.title}
                            </h4>
                            <p className={`text-sm leading-relaxed relative z-10 transition-colors duration-300 ${isActive ? 'text-white' : 'text-neutral-400 group-hover:text-neutral-200'}`}>
                                {item.description}
                            </p>

                            {/* Decorative Corner for Active State */}
                            {isActive && (
                                <div className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-[#faaf40] animate-pulse" />
                            )}
                        </motion.div>
                    );
                })}
            </div>
        </div>

      </div>
    </section>
  );
}